/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, func, object, shape, string,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { deepObjectExtend } from '../../../utils/object';
import { main as mainStyle } from '../../../style/main';

const styles = ({ typography, palette }) => deepObjectExtend(mainStyle(typography, palette), {
    DialogWrapper: {
        '&.desktop': {
            // Used contains Mui class name to avoid CSS issue on Account MFE
            '& [class*="MuiDialog-container"]': {
                '& [class*="MuiDialog-paper"]': {
                    borderRadius: 0,
                },
            },
            '& .DialogTitle': {
                display: 'none',
            },
            '& .DialogContent': {
                margin: '24px',
                '& p': {
                    color: palette.grey1,
                },
            },
            '& .DialogAction': {
                'flex-direction': 'row',
            },
        },
        '& .DialogTitle': {
            margin: '24px',
            padding: 0,
            '& h2': {
                fontSize: '14px',
                textTransform: 'uppercase',
                fontWeight: 700,
            },
        },
        '& .DialogContent': {
            margin: '0 24px 24px 24px',
            padding: 0,
            '& p': {
                color: palette.grey1,
                fontSize: '16px',
            },
        },
        '& .DialogAction': {
            justifyContent: 'center',
            flexDirection: 'column-reverse',
            padding: 0,
            margin: '0 24px 24px 24px',
        },
        '& button': {
            fontFamily: typography.fontFamily,
            boxSizing: 'border-box',
            WebkitFontSmoothing: 'antialiased',
            fontWeight: '700',
            width: 'auto',
            minWidth: 150,
            fontSize: '1em',
            textTransform: 'none',
            borderRadius: '20px',
            padding: '6px 40px',
            'box-shadow': 'none',
            '&.primary': {
                color: palette.common.white,
                backgroundColor: palette.primaryButton,
                border: `1px solid ${palette.primaryButton}`,
                '&:hover': {
                    color: palette.common.white, // TODO
                    border: '1px solid #d73833', // TODO
                    'background-color': '#d73833', // TODO
                },
                '&[disabled]': {
                    color: '#C6C6C6', // TODO
                    border: `1px solid ${palette.grey['300'] || '#E0E0E0'}`,
                    'background-color': `${palette.grey['300'] || '#E0E0E0'}`,
                },
            },
            '&.secondary': {
                color: '#161616',
                backgroundColor: palette.common.white,
                border: '1px solid #161616',
                marginRight: 24,
                '&:hover': {
                    color: palette.common.white,
                    border: '1px solid #161616', // TODO
                    'background-color': '#161616', // TODO
                },
                '&[disabled]': {
                    color: '#C6C6C6', // TODO
                    border: `1px solid ${palette.grey['300'] || '#E0E0E0'}`,
                    'background-color': `${palette.grey['300'] || '#E0E0E0'}`,
                },
            },
        },
        '&:not(.desktop)': {
            '& button': {
                textTransform: 'uppercase',
                '&.primary': {
                    width: '100%',
                },
                '&.secondary': {
                    'background-color': palette.cta1,
                    color: palette.common.white,
                    border: `1px solid ${palette.cta1}`,
                    marginRight: 0,
                    marginTop: 12,
                    fontSize: '14px',
                    minWidth: 50,
                    '&:hover': {
                        color: palette.common.white,
                        'background-color': palette.cta1,
                    },
                },
            },
        },
    },
});

function SubscriptionDialog({
    classes, open, handleClose, handleConfirm, messages, isDesktop,
}) {
    const desktopClass = isDesktop ? 'desktop' : '';
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown
            aria-labelledby="Subscription-Cancel-Dialog"
            aria-describedby="Cancel-this-Subscription-yes-no"
            className={`${classes.DialogWrapper} ${desktopClass}`}
        >
            <DialogTitle className="DialogTitle">{messages.title}</DialogTitle>
            <DialogContent className="DialogContent">
                <DialogContentText>
                    {messages.body}
                </DialogContentText>
            </DialogContent>
            <DialogActions className="DialogAction">
                <Button disableFocusRipple onClick={handleClose} className="secondary" autoFocus>
                    <strong>{messages.cancel}</strong>
                </Button>
                <Button disableFocusRipple onClick={handleConfirm} className="primary">
                    <strong>{messages.confirm}</strong>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

SubscriptionDialog.defaultProps = {
    isDesktop: false,
};

SubscriptionDialog.propTypes = {
    classes: object.isRequired,
    handleClose: func.isRequired,
    handleConfirm: func.isRequired,
    open: bool.isRequired,
    messages: shape({
        title: string.isRequired,
        body: string.isRequired,
        cancel: string.isRequired,
        confirm: string.isRequired,
    }).isRequired,
    isDesktop: bool,
};

export default withStyles(styles, { withTheme: true })(SubscriptionDialog);
